import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { X, Loader2, CheckCircle, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const PERMISSION_LABELS = {
  view_all_deals: { label: 'View all team deals', desc: 'See deals assigned to other reps' },
  edit_commissions: { label: 'Edit commission rates', desc: 'Change % on individual deals' },
  delete_deals: { label: 'Delete deals', desc: 'Permanently remove deals' },
  manage_team: { label: 'Manage team members', desc: 'Invite, edit, deactivate reps' },
  manage_permissions: { label: 'Manage permissions', desc: 'Change roles & permissions of others' },
  export_data: { label: 'Export data', desc: 'Download CSVs of deals/contracts' },
  import_leads: { label: 'Import leads (CSV)', desc: 'Bulk upload from Apollo.io etc.' },
  manage_rates: { label: 'Manage energy rates', desc: 'Override state-level rate defaults' },
  view_all_contracts: { label: 'View all active contracts', desc: 'Renewal tracker (all reps)' },
  view_security_logs: { label: 'View security logs', desc: 'Audit log for login/permissions' },
  send_emails_as_user: { label: 'Send email via Outlook', desc: 'Personal email integration' },
  view_commissions: { label: 'View commission amounts', desc: 'See own $ earned' },
  manage_renewals: { label: 'Start contract renewals', desc: 'Clone contracts into new deals' },
};

const ROLE_OPTIONS = [
  { value: 'junior_rep', label: 'Junior Rep' },
  { value: 'senior_rep', label: 'Senior Rep' },
  { value: 'sales_rep', label: 'Sales Rep (legacy)' },
  { value: 'team_lead', label: 'Team Lead' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

export const PermissionsModal = ({ member, onClose, onSaved }) => {
  const [role, setRole] = useState(member.role);
  const [perms, setPerms] = useState(member.permissions || {});
  const [rolesData, setRolesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    ax.get('/api/admin/permissions/roles').then(({ data }) => {
      if (!mounted) return;
      setRolesData(data);
      setLoading(false);
    }).catch(err => {
      if (!mounted) return;
      setError(err.response?.data?.detail || 'Failed to load roles');
      setLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  const roleDefaults = rolesData?.roles.find(r => r.id === role)?.defaults || {};
  const effective = { ...roleDefaults, ...perms };

  const togglePerm = (key) => {
    setPerms(prev => ({ ...prev, [key]: !effective[key] }));
  };

  const resetOverrides = () => setPerms({});

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      // Only send actual overrides that differ from role defaults
      const overrides = {};
      Object.keys(perms).forEach(k => {
        if (perms[k] !== roleDefaults[k]) overrides[k] = perms[k];
      });
      await ax.put(`/api/admin/team/${member._id}/permissions`, {
        role,
        permissions: overrides,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} data-testid="permissions-modal">
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Shield size={20} style={{ color: 'var(--teal)' }} /> Access & Permissions
            </h2>
            <p style={{ fontSize: '0.813rem', color: '#6B7280', margin: '2px 0 0' }}>{member.name} • {member.email}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }} data-testid="perms-modal-close">
            <X size={22} />
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <Loader2 size={28} className="animate-spin" style={{ color: 'var(--teal)' }} />
          </div>
        ) : (
          <>
            <div style={{ padding: '1.25rem 1.5rem', overflow: 'auto', flex: 1 }}>
              {/* Role selector */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 6 }}>Role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: 6, background: '#fff', fontSize: '0.875rem' }}
                  data-testid="perms-role-select"
                >
                  {ROLE_OPTIONS.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
                </select>
                <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: 4 }}>
                  Setting a role loads default permissions. Toggle below to override.
                </div>
              </div>

              {/* Permissions grid */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>Permissions</label>
                {Object.keys(perms).length > 0 && (
                  <button onClick={resetOverrides} style={{ fontSize: '0.75rem', color: 'var(--teal)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }} data-testid="perms-reset">
                    Reset to role defaults
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {(rolesData?.permissions || []).map((key) => {
                  const meta = PERMISSION_LABELS[key] || { label: key, desc: '' };
                  const isOn = !!effective[key];
                  const isOverride = perms[key] !== undefined && perms[key] !== roleDefaults[key];
                  return (
                    <label
                      key={key}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 0.875rem',
                        border: `1px solid ${isOverride ? 'var(--teal)' : '#E5E7EB'}`, borderRadius: 8, cursor: 'pointer',
                        background: isOverride ? '#F0FDFA' : '#fff', transition: 'all 0.15s',
                      }}
                      data-testid={`perm-${key}`}
                    >
                      <input type="checkbox" checked={isOn} onChange={() => togglePerm(key)} style={{ width: 18, height: 18, accentColor: 'var(--teal)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--navy)' }}>
                          {meta.label}
                          {isOverride && <span style={{ marginLeft: 8, fontSize: '0.65rem', background: 'var(--teal)', color: '#fff', padding: '1px 6px', borderRadius: 100 }}>OVERRIDE</span>}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{meta.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {error && <div style={{ padding: '0.75rem 1.5rem', color: '#991B1B', fontSize: '0.875rem', background: '#FEE2E2' }}>{error}</div>}

            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <Button onClick={onClose} className="btn-secondary" data-testid="perms-cancel">Cancel</Button>
              <Button onClick={save} className="btn-primary" disabled={saving} data-testid="perms-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                <span style={{ marginLeft: 6 }}>Save Permissions</span>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
